import { useCallback, useRef } from 'react';

import type { CallDevices } from '#features/calls/hooks/useCallRoom';
import { useCallStore } from '#features/calls/model/callStore';
import { useNativeCallSnapshot } from '#features/calls/utils/nativeCalls';
import { openAppSettings } from '#features/notifications/utils/deviceSettings';

const devices: CallDevices = {
  microphones: [],
  speakers: [],
  cameras: [],
  activeMicrophoneId: null,
  activeSpeakerId: null,
  activeCameraId: null,
  speakerSelectionSupported: false,
};
const noop = () => {};
const unavailable = async () => false;

export const useNativeCallRoom = () => {
  const phase = useCallStore((state) => state.phase);
  const callId = useCallStore((state) => state.call?.callId);
  const snapshot = useNativeCallSnapshot((state) => state.snapshot);
  const native = snapshot?.call?.id === callId ? snapshot : null;
  const media = native?.media;
  const remoteCameraRef = useRef<HTMLVideoElement | null>(null);
  const remoteScreenRef = useRef<HTMLVideoElement | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const localScreenRef = useRef<HTMLVideoElement | null>(null);
  const requestMicAccess = useCallback(async () => {
    await openAppSettings();
    return false;
  }, []);

  return {
    remoteCameraRef,
    remoteScreenRef,
    localVideoRef,
    localScreenRef,
    microphoneTrack: null as MediaStreamTrack | null,
    connectionState: media?.connectionState ?? ('idle' as const),
    remoteJoined: media?.peerConnected ?? false,
    remoteVideoOn: false,
    remoteScreenOn: false,
    remoteMicOn: true,
    localSpeaking: false,
    remoteSpeaking: false,
    peerGone: phase === 'active' && media?.connectionState === 'connected' && !media.peerConnected,
    screenSharing: false,
    screenSharePending: false,
    screenShareHint: null,
    mediaError: media?.micDenied ?? false,
    camDenied: false,
    requestMicAccess,
    requestCameraAccess: unavailable,
    audioBlocked: false,
    unblockAudio: async () => {},
    devices,
    switchDevice: unavailable,
    refreshDevices: async () => {},
    toggleScreenShare: async () => {},
    attachPipVideo: (_element: HTMLVideoElement) => null,
    detachPipVideo: noop,
  };
};
